import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { blendApi, UserBlend } from "./api";

export default function MyBlendsPage() {
  const nav = useNavigate();
  const [blends, setBlends] = useState<UserBlend[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editNotes, setEditNotes] = useState("");

  const load = () => blendApi.blends().then(setBlends).catch((e) => setErr(String(e.message || e))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const del = async (id: string) => {
    try { await blendApi.deleteBlend(id); toast.success("اتحذفت"); load(); }
    catch (e: any) { toast.error(String(e.message || e)); }
  };

  const saveEdit = async (id: string) => {
    try {
      await blendApi.updateBlend(id, { name: editName, notes: editNotes });
      toast.success("اتعدّلت");
      setEditing(null);
      load();
    } catch (e: any) { toast.error(String(e.message || e)); }
  };

  const openInCalc = (b: UserBlend) => {
    sessionStorage.setItem("blend_handoff", JSON.stringify({
      name: b.name, base_target_id: b.base_target_id, blend_id: b.id, body: b.body, direction: b.direction,
      boosters: b.boosters, concentration: b.concentration, batch: b.batch || null, warnings: b.warnings || [],
    }));
    nav("/pureh/blend/calculator");
  };

  if (loading) return <div className="bl-sub py-8 text-center" data-testid="myblends-loading">جارٍ التحميل…</div>;
  if (err) return <div className="bl-sub py-8 text-center" style={{ color: "var(--bl-danger)" }} data-testid="myblends-error">{err}</div>;
  if (!blends.length) {
    return (
      <div className="bl-card text-center py-10" data-testid="myblends-empty">
        <div className="font-bold">مفيش تركيبات محفوظة لسه</div>
        <div className="bl-sub mt-1">اختار هدف من «استكشاف» واحفظ التوصية — هتلاقيها هنا.</div>
      </div>
    );
  }

  return (
    <div className="bl-grid" data-testid="myblends-list">
      {blends.map((b) => (
        <div key={b.id} className="bl-card" data-testid={`blend-row-${b.id}`}>
          {editing === b.id ? (
            <div className="space-y-2">
              <input className="bl-input" value={editName} onChange={(e) => setEditName(e.target.value)} data-testid={`blend-edit-name-${b.id}`} />
              <textarea className="bl-input" rows={2} placeholder="ملاحظات…" value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)} data-testid={`blend-edit-notes-${b.id}`} />
              <div className="flex gap-2">
                <button className="bl-btn primary" onClick={() => saveEdit(b.id)} data-testid={`blend-save-edit-${b.id}`}>حفظ</button>
                <button className="bl-btn ghost" onClick={() => setEditing(null)}>إلغاء</button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                {b.code && <span className="bl-pill bl-en" data-testid={`blend-code-${b.id}`}>{b.code}</span>}
                <span className="font-extrabold">{b.name}</span>
                {b.status && <span className="bl-pill" data-testid={`blend-status-${b.id}`}>{b.status}</span>}
              </div>
              <div className="bl-sub bl-en mt-1">{b.body?.oil} {b.body?.pct}% + {b.direction?.oil} {b.direction?.pct}%</div>
              <div className="bl-sub mt-1">{(b.boosters || []).length} مُعزّز · تركيز {b.concentration || "—"}</div>
              {b.batch && (
                <div className="bl-sub mt-1" data-testid={`blend-batch-snapshot-${b.id}`}>
                  🧪 {b.batch.bottles} × {b.batch.sizeMl} مل · تكلفة/زجاجة {b.batch.costPerBottle} {b.batch.currency} · هامش {b.batch.margin}%
                </div>
              )}
              {b.notes && <div className="bl-sub mt-1">📝 {b.notes}</div>}
              <div className="flex gap-2 mt-3 flex-wrap">
                <button className="bl-btn primary" onClick={() => nav(`/pureh/blend/detail/${b.id}`)} data-testid={`blend-detail-${b.id}`}>الكارت الكامل</button>
                <button className="bl-btn ghost" onClick={() => openInCalc(b)} data-testid={`blend-to-calc-${b.id}`}>▶️ الحاسبة</button>
                <button className="bl-btn ghost" data-testid={`blend-edit-${b.id}`}
                  onClick={() => { setEditing(b.id); setEditName(b.name); setEditNotes(b.notes || ""); }}>تعديل</button>
                {b.base_target_id && (
                  <button className="bl-btn ghost" onClick={() => nav(`/pureh/blend/target/${b.base_target_id}`)}
                    data-testid={`blend-back-to-target-${b.id}`}>رجوع للهدف</button>
                )}
                <button className="bl-btn danger" onClick={() => del(b.id)} data-testid={`blend-delete-${b.id}`}>حذف</button>
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}
